import { useEffect, useState } from "react";

// 3D BOARD (R5): a building of 1 to 3 floors drawn in a 98 x 98 box scaled to the square's top
// face. Body x 18..80 standing on a base at y = 88 (2.5 px plinth in the owner's dark colour);
// each floor a 12 px band in the owner's wall colour, floors separated by a 1.2 px dark line;
// four 9 x 6 windows per floor (x 23 / 37 / 51 / 65, 3.5 below the floor line); a 30 px roof
// in the owner's roof colour on the top floor with a 1 px inset outline 4 in from its edges and a
// 1.4 px line where it meets the walls; shadow offset left by 2 + 2 x floors.
const BODY_X = 18;
const BODY_W = 62;
const WIN_X = [23, 37, 51, 65];
const PLANTS = [[28, 7], [40, 5], [54, 9], [66, 6], [34, 15], [48, 16], [62, 15]];
const FLOOR = 12;
const GROUND = 88;
const ROOF = 30;
const PLINTH = 2.5;
// v3 step 7: the square now has a road and sidewalk round the plot (StreetTile), so the building is
// drawn a little smaller to stand on the plot (ground line y ~ 84 of 100, clear of the sidewalk).
const STREET_SCALE = 0.9;
// Safety bound only (the real cap: 3 floors on every square, TERRAIN 3 of 3; older games may hold taller stacks).
export const MAX_DRAWN_FLOORS = 8;

function Block({ f, shade, greenRoof }) {
  const wallY = GROUND - FLOOR * f;
  const roofY = wallY - ROOF;
  const floors = Array.from({ length: f });
  return (
    <>
      <rect x={BODY_X} y={wallY} width={BODY_W} height={FLOOR * f} fill={shade.wall} />
      {floors.slice(1).map((_, k) => (
        <rect key={`line-${k}`} x={BODY_X} y={GROUND - FLOOR * (k + 1) - 0.6} width={BODY_W} height={1.2} fill={shade.edge} />
      ))}
      {floors.map((_, n) =>
        WIN_X.map((x) => (
          <rect
            key={`${n}-${x}`}
            data-testid={`window-${n + 1}-${x}`}
            x={x}
            y={GROUND - FLOOR * (n + 1) + 3.5}
            width={9}
            height={6}
            rx={0.6}
            fill="#2B3236"
            opacity={0.78}
          />
        )),
      )}
      <rect x={BODY_X} y={GROUND - PLINTH} width={BODY_W} height={PLINTH} fill={shade.edge} />
      <rect x={BODY_X} y={roofY} width={BODY_W} height={ROOF} fill={shade.roof} />
      <rect x={BODY_X + 4} y={roofY + 4} width={BODY_W - 8} height={ROOF - 8} fill="none" stroke={shade.edge} strokeOpacity={0.35} strokeWidth={1} />
      {greenRoof && (
        <g data-testid="green-roof-token">
          <rect x={BODY_X + 4.5} y={roofY + 4.5} width={BODY_W - 9} height={ROOF - 9} fill="#6E8B5A" />
          {PLANTS.map(([x, dy]) => (
            <circle key={`${x}-${dy}`} cx={x} cy={roofY + 4 + dy} r={1.6} fill="#4F6B40" />
          ))}
        </g>
      )}
      <line x1={BODY_X} x2={BODY_X + BODY_W} y1={wallY} y2={wallY} stroke={shade.edge} strokeWidth={1.4} />
    </>
  );
}

function Shadow({ f }) {
  const roofY = GROUND - FLOOR * f - ROOF;
  return <rect x={BODY_X - (2 + 2 * f)} y={roofY} width={BODY_W} height={GROUND - roofY} fill="rgba(42,46,44,0.16)" />;
}

// mode: "rise" (new storey slides up), "fall" (whole building slides down), "ghost" (next-floor preview).
// Never takes clicks (pointer-events: none).
export default function Building({ cellKey, floors, shade, greenRoof = false, riseFrom = null, mode = null }) {
  const f = Math.max(1, Math.min(MAX_DRAWN_FLOORS, floors));
  const [rising, setRising] = useState(riseFrom !== null && riseFrom < floors);
  useEffect(() => {
    if (!rising) return undefined;
    const t = setTimeout(() => setRising(false), 200);
    return () => clearTimeout(t);
  }, [rising]);
  const clipId = `bclip-${cellKey}-${mode || "b"}`;
  const anim = mode === "fall"
    ? { animation: "floor-fall 180ms ease-in forwards", "--fall": `${FLOOR * f + ROOF}px` }
    : rising
      ? { animation: "floor-rise 180ms ease-out", "--rise": `${FLOOR * (floors - (riseFrom ?? floors - 1))}px` }
      : undefined;
  return (
    <svg
      viewBox="0 0 98 98"
      className="absolute inset-0 w-full h-full pointer-events-none"
      data-testid={mode === "ghost" ? `building-ghost-${cellKey}` : `cube-stack-${f}`}
      data-floors={f}
      data-stack={floors}
      data-anim={mode === "fall" ? "fall" : rising ? "rise" : undefined}
      style={{ opacity: mode === "ghost" ? 0.4 : 1, overflow: "visible", zIndex: 4, transform: `scale(${STREET_SCALE})`, transformOrigin: "50% 40%" }}
      aria-hidden="true"
    >
      <defs>
        {/* Clips only below ground (rise / fall animations); open upwards for tall stacks. */}
        <clipPath id={clipId}>
          <rect x={-40} y={-120} width={178} height={GROUND + 120} />
        </clipPath>
      </defs>
      {mode !== "ghost" && (
        <g style={mode === "fall" ? { animation: "demolish-fade 180ms ease-in forwards" } : rising ? { animation: "shadow-grow 180ms ease-out" } : undefined}>
          <Shadow f={f} />
        </g>
      )}
      <g clipPath={`url(#${clipId})`}>
        <g style={anim}>
          <Block f={f} shade={shade} greenRoof={greenRoof} />
        </g>
      </g>
    </svg>
  );
}
